import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { ritentaSoloLeLetture } from '@/lib/ritentaSoloLeLetture';
import { guardiaOspite } from '@/services/guardiaOspite';
import { getQbotAccessToken } from '@/lib/logto';
import { getLimuloToken } from '@/lib/limulo/session';
import { limuloIsTheSourceNow } from '@/lib/limulo/sorgente';
import { qbotRiceveLaSessioneLimulo } from '@/lib/qbotDestinazione';
import type {
  AdvisorDecksResponse,
  AdvisorUpgradesResponse,
  BattlesResponse,
  CoachBuildResponse,
  CoachDeckResponse,
  CoachMatchupResponse,
  CompleteDeckResponse,
  CounterDeckResponse,
  CrIdentity,
  DeckCheckResponse,
  DeckRateResponse,
  DoctorSeedCard,
  LinkPlayerResponse,
  MetaCardsResponse,
  NotificationConsentPatch,
  NotificationConsentResponse,
  NotificationConsentRow,
  PlayerByTagResponse,
  PopularDecksResponse,
  ProfileResponse,
  PublicCard,
  SavedDeck,
} from '@/types/qbot';

/** L'header con cui QBot riceve la sessione Limulo (concordato con qbot-stg-orcr, 29/09). */
const LIMULO_AUTH_HEADER = 'X-Limulo-Authorization';

/**
 * QBot API client. Talks to qbot-api at /api/v1/cr/*.
 *
 * Auth: Logto Bearer JWT with audience `https://qbot.qlash.gg`, minted
 * by getQbotAccessToken() via refresh-token grant (same pattern as
 * qriotsApi). The audience is the QBot backoffice resource — already
 * registered on the staging+prod Logto tenants — so no new Logto setup
 * is required for the Mini App.
 *
 * Dev: env.qbotApiUrl = '/qbot-api'; the Vite proxy forwards to `VITE_QBOT_API_URL`, or to QBot
 * PRODUCTION when it is unset (`vite.config.ts`, same default as `env.ts`; it also strips the Limulo session header unless its target is staging) — NOT to the
 * MacMini, as this line used to say. `env.qbotDestinationUrl` is where a request really goes.
 * Prod: env.qbotApiUrl = 'https://qbot.qlash.gg'.
 *
 * Tag invalidation:
 *   - 'Identity' refetches `/me` after link/unlink
 *   - 'Profile' refetches `/profile/me` after link
 *   - 'SavedDecks' refetches `/decks/saved` after create/delete
 */
export const qbotApi = createApi({
  reducerPath: 'qbotApi',
  baseQuery: guardiaOspite('qbot', ritentaSoloLeLetture(
    fetchBaseQuery({
      baseUrl: env.qbotApiUrl,
      prepareHeaders: async (headers, { getState }) => {
        const token = await getQbotAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        // 🔴 La sessione Limulo va a QBot SOLO da qui (la baseQuery di `env.qbotApiUrl`) e SOLO
        // quando Limulo e' la fonte (flag + ruolo `beta`, `lib/limulo/sorgente.ts`): QBot la usa per
        // ripiegare sul profilo Limulo di chi non ha un `SavedPlayer` (il Sit&Go scrive su Limulo).
        // Fuori dal ramo, e su ogni altra base URL, l'header NON parte. E' una credenziale
        // di un altro servizio: mai in log, mai nel testo di un errore — e se la sessione non si
        // ottiene si tace e la richiesta parte senza, invece di rompere una lettura che funzionava.
        // ⛔ Due condizioni in AND oltre al ramo, dalla revisione di #506:
        //  · `authed` — LO STESSO `bridgeAuthenticated` che guarda `useLogtoRoles`: senza sessione i
        //    ruoli sono `[]`, anche se in `localStorage` resta un token con `beta`;
        //  · SOLO verso QBot di STAGING, giudicato sulla DESTINAZIONE VERA (`env.qbotDestinationUrl`), non
        //    su `env.qbotApiUrl`: in dev quello e' `/qbot-api`, il proxy Vite, che manda a
        //    `VITE_QBOT_API_URL || prod` da server a server (nessun preflight CORS a fermarlo).
        //    `.env.staging-prodtenant` e un `.env.local` copiato da `.env.example` puntano a prod.
        //    Si AMMETTE un host, non si esclude l'altro: chi non riconosce non riceve la sessione.
        const authed =
          (getState() as { auth?: { bridgeAuthenticated?: boolean } }).auth?.bridgeAuthenticated === true;
        if (limuloIsTheSourceNow(authed) && qbotRiceveLaSessioneLimulo(env.qbotDestinationUrl)) {
          try {
            const limuloToken = await getLimuloToken();
            if (limuloToken) headers.set(LIMULO_AUTH_HEADER, `Bearer ${limuloToken}`);
          } catch {
            // Deliberatamente muto: il messaggio di un errore di sessione non deve portare il token.
          }
        }
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
  )),
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ['Identity', 'Profile', 'SavedDecks', 'Consent'],
  endpoints: (builder) => ({
    // ─── Identity + card catalog ────────────────────────────────────────

    me: builder.query<CrIdentity, void>({
      query: () => '/api/v1/cr/me',
      transformResponse: (raw: { data: CrIdentity }) => raw.data,
      providesTags: ['Identity'],
    }),

    cards: builder.query<PublicCard[], void>({
      query: () => '/api/v1/cr/cards',
      transformResponse: (raw: { data: { cards: PublicCard[]; count: number } }) => raw.data.cards,
      keepUnusedDataFor: 86400, // 24h — catalog is near-static
    }),

    // ─── Profile ────────────────────────────────────────────────────────

    profileMe: builder.query<ProfileResponse, void>({
      query: () => '/api/v1/cr/profile/me',
      transformResponse: (raw: { data: ProfileResponse }) => raw.data,
      providesTags: ['Profile'],
    }),

    profileByTag: builder.query<ProfileResponse, { tag: string }>({
      query: ({ tag }) => `/api/v1/cr/profile/${encodeURIComponent(tag)}`,
      transformResponse: (raw: { data: ProfileResponse }) => raw.data,
    }),

    /**
     * Identity bridge (CR side, PRIMARY): resolve a Clash Royale tag → the
     * owner's Logto `sub` (GET /api/v1/cr/players/by-tag/:tag). Broader source
     * than qduels' link table — QBot maps any player it has seen (SavedPlayer
     * cache, read-through-seeded from the QLASH platform), so this resolves
     * more opponents. The qduels query is the fallback when this misses.
     *
     * 404 `{ error: { code: "NOT_FOUND" } }` (tag not mapped) and 400
     * `{ error: { code: "INVALID_TAG" } }` are NOT hard failures — they stay
     * on the error channel so callers read them as "not resolved" and try the
     * qduels fallback (then render no social buttons if that misses too). The
     * baseQuery does not retry 4xx (`lib/ritentaSoloLeLetture.ts`: one retry, reads only, transient errors only —
     * until that helper existed this line was false and every 4xx was retried once).
     */
    playerByTag: builder.query<PlayerByTagResponse, string>({
      query: (tag) => `/api/v1/cr/players/by-tag/${encodeURIComponent(tag)}`,
      transformResponse: (raw: { data: PlayerByTagResponse }) => raw.data,
      keepUnusedDataFor: 600,
    }),

    linkPlayer: builder.mutation<LinkPlayerResponse, { tag: string }>({
      query: (body) => ({ url: '/api/v1/cr/profile/link', method: 'POST', body }),
      transformResponse: (raw: { data: LinkPlayerResponse }) => raw.data,
      invalidatesTags: ['Identity', 'Profile'],
    }),

    // ⛔ NON e' lo «Scollega» del ramo Limulo. Se il tag viene da Limulo (`linkedCrTagSource: 'limulo'`
    // su `/cr/me`, oppure `limuloIsTheSource`), questa DELETE fallisce senza la sessione del bot e
    // dopo ~15' il tag RICOMPARE: lo scollegamento li' deve ARCHIVIARE il profilo su Limulo
    // (`PATCH /v1/games/profiles/:id` con `status: 'archived'` — `inactive` non e' uno stato di Limulo e
    // il PATCH lo scarta; `updateLimuloProfile`). Oggi non ha consumatori nella TMA
    // (`git grep useUnlinkPlayerMutation -- src` → solo questa definizione): chi ne aggiunge uno
    // gestisca prima quel ramo (contratto QBot #251, 29/09).
    unlinkPlayer: builder.mutation<{ unlinked: boolean; removedRows: number }, void>({
      query: () => ({ url: '/api/v1/cr/profile/link', method: 'DELETE' }),
      transformResponse: (raw: { data: { unlinked: boolean; removedRows: number } }) => raw.data,
      invalidatesTags: ['Identity', 'Profile'],
    }),

    // ─── Decks ──────────────────────────────────────────────────────────

    popularDecks: builder.query<
      PopularDecksResponse,
      { archetype?: string; cards?: string[]; page?: number; pageSize?: number }
    >({
      query: ({ archetype, cards, page = 1, pageSize = 10 }) => ({
        url: '/api/v1/cr/decks/popular',
        params: {
          ...(archetype ? { archetype } : {}),
          ...(cards?.length ? { cards: cards.join(',') } : {}),
          page,
          pageSize,
        },
      }),
      transformResponse: (raw: { data: PopularDecksResponse }) => raw.data,
      keepUnusedDataFor: 1800, // 30 min — popular decks are server-side cached for 30 min too
    }),

    deckCheck: builder.query<DeckCheckResponse, { tag: string }>({
      query: ({ tag }) => `/api/v1/cr/decks/check/${encodeURIComponent(tag)}`,
      transformResponse: (raw: { data: DeckCheckResponse }) => raw.data,
    }),

    savedDecks: builder.query<{ decks: SavedDeck[]; count: number }, void>({
      query: () => '/api/v1/cr/decks/saved',
      transformResponse: (raw: { data: { decks: SavedDeck[]; count: number } }) => raw.data,
      providesTags: ['SavedDecks'],
    }),

    saveDeck: builder.mutation<{ deck: SavedDeck }, { name: string; cards: string[] }>({
      query: (body) => ({ url: '/api/v1/cr/decks/saved', method: 'POST', body }),
      transformResponse: (raw: { data: { deck: SavedDeck } }) => raw.data,
      invalidatesTags: ['SavedDecks'],
    }),

    deleteSavedDeck: builder.mutation<{ deleted: boolean; id: number }, { id: number }>({
      query: ({ id }) => ({ url: `/api/v1/cr/decks/saved/${id}`, method: 'DELETE' }),
      transformResponse: (raw: { data: { deleted: boolean; id: number } }) => raw.data,
      invalidatesTags: ['SavedDecks'],
    }),

    // ─── Battles ────────────────────────────────────────────────────────

    battles: builder.query<
      BattlesResponse,
      { tag: string; page?: number; pageSize?: number; analyze?: boolean; fresh?: boolean }
    >({
      // `fresh=true` (sent by the Refresh button) asks qbot to bypass its cache
      // + the qCrC collector and read the battle log directly from Supercell —
      // the only way to surface a just-finished match before the collector's
      // ~6-min cycle. It's also a distinct RTK cache key, so switching to fresh
      // forces a fetch instead of serving the cached collector list.
      query: ({ tag, page = 1, pageSize = 10, analyze = false, fresh = false }) => ({
        url: `/api/v1/cr/battles/${encodeURIComponent(tag)}`,
        params: {
          page,
          pageSize,
          // `analyze` + `BattlesResponse.analysis` document a still-existing
          // server contract; the TMA no longer sends/reads it (analyze toggle
          // was removed from the UI). Retained for parity, not currently used.
          ...(analyze ? { analyze: 'true' } : {}),
          ...(fresh ? { fresh: 'true' } : {}),
        },
      }),
      transformResponse: (raw: { data: BattlesResponse }) => raw.data,
    }),

    // ─── AI coaching (pro tier required) ────────────────────────────────

    coachDeck: builder.mutation<CoachDeckResponse, { tag: string }>({
      query: ({ tag }) => ({
        url: `/api/v1/cr/coach/deck/${encodeURIComponent(tag)}`,
        method: 'POST',
      }),
      transformResponse: (raw: { data: CoachDeckResponse }) => raw.data,
    }),

    /**
     * Analyse an ARBITRARY deck (list of card names) rather than the player's
     * current deck. Powers the per-deck "AI analysis" button on the saved /
     * popular deck lists where there's no tag to anchor the analysis to.
     *
     * Returns the same `CoachDeckResponse` shape as `coachDeck` (server reuses
     * the same coach prompt, just seeded from the card list). Pro-gated +
     * rate-limited server-side, same as the other coach endpoints.
     *
     * NOTE: the QBot endpoint `POST /api/v1/cr/coach/deck-list` is being built
     * in parallel — wired here ahead of the deploy.
     */
    coachDeckList: builder.mutation<CoachDeckResponse, { cards: string[] }>({
      query: (body) => ({ url: '/api/v1/cr/coach/deck-list', method: 'POST', body }),
      transformResponse: (raw: { data: CoachDeckResponse }) => raw.data,
    }),

    coachBuild: builder.mutation<CoachBuildResponse, { style?: string; keptCards?: string[] }>({
      query: (body) => ({ url: '/api/v1/cr/coach/build', method: 'POST', body }),
      transformResponse: (raw: { data: CoachBuildResponse }) => raw.data,
    }),

    coachMatchup: builder.mutation<
      CoachMatchupResponse,
      { myTag: string; opponentTag: string; gameMode?: string }
    >({
      query: (body) => ({ url: '/api/v1/cr/coach/matchup', method: 'POST', body }),
      transformResponse: (raw: { data: CoachMatchupResponse }) => raw.data,
    }),

    // ─── Deck rating (arbitrary 8-card deck — deck builder) ─────────────
    rateDeck: builder.mutation<DeckRateResponse, { cards: Array<string | number> }>({
      query: (body) => ({ url: '/api/v1/cr/decks/rate', method: 'POST', body }),
      transformResponse: (raw: { data: DeckRateResponse }) => raw.data,
    }),

    // ─── Advisor / deck-doctor (qCrC, deterministic, free-tier) ─────────
    // Operate on the caller's linked CR tag (resolved server-side); 404
    // NOT_LINKED when no tag. Can trigger a live upstream fetch → slow (~15s).
    advisorUpgrades: builder.query<
      AdvisorUpgradesResponse,
      { segment?: string; limit?: number } | void
    >({
      query: (args) => ({
        url: '/api/v1/cr/advisor/upgrades',
        params: {
          ...(args?.segment ? { segment: args.segment } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
        },
      }),
      transformResponse: (raw: { data: AdvisorUpgradesResponse }) => raw.data,
    }),

    advisorDecks: builder.query<AdvisorDecksResponse, { segment?: string; limit?: number } | void>({
      query: (args) => ({
        url: '/api/v1/cr/advisor/decks',
        params: {
          ...(args?.segment ? { segment: args.segment } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
        },
      }),
      transformResponse: (raw: { data: AdvisorDecksResponse }) => raw.data,
    }),

    completeDeck: builder.mutation<
      CompleteDeckResponse,
      { seedCards?: DoctorSeedCard[]; mode?: string; archetype?: string }
    >({
      query: (body) => ({ url: '/api/v1/cr/advisor/complete-deck', method: 'POST', body }),
      transformResponse: (raw: { data: CompleteDeckResponse }) => raw.data,
    }),

    counterDeck: builder.mutation<
      CounterDeckResponse,
      { opponentDeck: Array<string | number>; mode?: string }
    >({
      query: (body) => ({ url: '/api/v1/cr/advisor/counter-deck', method: 'POST', body }),
      transformResponse: (raw: { data: CounterDeckResponse }) => raw.data,
    }),

    // ─── Meta trend (best cards + risers/fallers) ───────────────────────
    // Best-effort upstream: returns { cards: [] } on error, never 429.
    metaCards: builder.query<
      MetaCardsResponse,
      { segment?: string; window?: string; limit?: number } | void
    >({
      query: (args) => ({
        url: '/api/v1/cr/meta/cards',
        params: {
          ...(args?.segment ? { segment: args.segment } : {}),
          ...(args?.window ? { window: args.window } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
        },
      }),
      transformResponse: (raw: { data: MetaCardsResponse }) => raw.data,
      keepUnusedDataFor: 1800, // 30 min — the meta refreshes every ~2h/6h
    }),

    // ─── Consenso alle notifiche per famiglia (ADR-0084 §3, §6-septies) ────

    /**
     * `GET /api/v1/me/notifications/consent` — una riga per (famiglia × canale), solo per le famiglie
     * che l'interruttore di QBot accende (`amici` manca finche' `notif.amiciEnabled` e' spenta;
     * `system` non c'e' mai). Si monta SOLO dalla pagina «Notifiche»: nessun altro la chiama.
     * `keepUnusedDataFor: 0` — un consenso non si serve da una cache: chi riapre la pagina rilegge.
     */
    notificationConsent: builder.query<NotificationConsentRow[], void>({
      query: () => '/api/v1/me/notifications/consent',
      transformResponse: (raw: { data: NotificationConsentResponse }) => raw.data.families,
      providesTags: ['Consent'],
      keepUnusedDataFor: 0,
    }),

    /**
     * `PATCH /api/v1/me/notifications/consent` — risponde con la riga RILETTA dal freno (non l'eco della
     * richiesta). La cache si aggiorna con quella riga e non con lo stato chiesto: se il server ha
     * detto altro, la pagina mostra l'altro. Nessuna GET di rilettura sul successo.
     * 409 (`consent_locked`, `optin_requires_declaration`) e 400 arrivano come errore con
     * `{ error: { code, message } }`: la pagina li traduce in frasi (`lib/notifiche/consenso.ts`).
     */
    setNotificationConsent: builder.mutation<NotificationConsentRow, NotificationConsentPatch>({
      query: (body) => ({ url: '/api/v1/me/notifications/consent', method: 'PATCH', body }),
      transformResponse: (raw: { data: NotificationConsentRow }) => raw.data,
      async onQueryStarted(patch, { dispatch, queryFulfilled }) {
        try {
          const { data: riga } = await queryFulfilled;
          dispatch(
            qbotApi.util.updateQueryData('notificationConsent', undefined, (righe) => {
              const i = righe.findIndex((r) => r.family === patch.family && r.channel === patch.channel);
              if (i >= 0) righe[i] = riga;
            }),
          );
        } catch {
          // Niente da disfare: la cache non e' stata toccata prima della risposta.
        }
      },
    }),
  }),
});

export const {
  useMeQuery,
  useCardsQuery,
  useProfileMeQuery,
  useProfileByTagQuery,
  usePlayerByTagQuery,
  useLinkPlayerMutation,
  useUnlinkPlayerMutation,
  usePopularDecksQuery,
  useDeckCheckQuery,
  useSavedDecksQuery,
  useSaveDeckMutation,
  useDeleteSavedDeckMutation,
  useBattlesQuery,
  useCoachDeckMutation,
  useCoachDeckListMutation,
  useCoachBuildMutation,
  useCoachMatchupMutation,
  useRateDeckMutation,
  useAdvisorUpgradesQuery,
  useAdvisorDecksQuery,
  useCompleteDeckMutation,
  useCounterDeckMutation,
  useMetaCardsQuery,
  useNotificationConsentQuery,
  useSetNotificationConsentMutation,
} = qbotApi;

/**
 * Build a CDN-ish URL for a card icon served by qbot-api.
 * Used by <CardIcon> to render PNG art without bundling the assets.
 */
export function qbotCardIconUrl(id: number, form: 'base' | 'evolution' | 'hero' = 'base'): string {
  const q = form === 'base' ? '' : `?form=${form}`;
  return `${env.qbotApiUrl}/api/v1/cr/cards/${id}/icon${q}`;
}
